import { useEffect, useState, useRef, useCallback } from 'react';
import { useLang } from '../../../i18n/LangContext';

// ── Ticker data ──────────────────────────────────────────────────────────────
const TICKERS = [
  { symbol: 'XAU/USD', price: '2 341.80', change: '+1.24%', up: true },
  { symbol: 'EUR/USD', price: '1.0842',   change: '+0.18%', up: true },
  { symbol: 'BTC/USD', price: '67 420.50',change: '+3.41%', up: true },
  { symbol: 'S&P 500', price: '5 248.30', change: '-0.32%', up: false },
  { symbol: 'WTI OIL', price: '82.14',    change: '-0.87%', up: false },
  { symbol: 'GBP/USD', price: '1.2731',   change: '+0.09%', up: true },
  { symbol: 'NASDAQ',  price: '18 340.65',change: '+0.76%', up: true },
  { symbol: 'USD/JPY', price: '154.62',   change: '-0.21%', up: false },
  { symbol: 'SILVER',  price: '27.43',    change: '+2.05%', up: true },
  { symbol: 'DAX 40',  price: '18 720.10',change: '+0.44%', up: true },
];

// ── Gold bar SVG component ───────────────────────────────────────────────────
function GoldBar({ style }: { style: React.CSSProperties }) {
  return (
    <div className="absolute pointer-events-none" style={style}>
      <svg width="64" height="32" viewBox="0 0 64 32" fill="none">
        <defs>
          <linearGradient id="goldTop" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#b8860b" />
            <stop offset="40%" stopColor="#f4d03f" />
            <stop offset="70%" stopColor="#d4af37" />
            <stop offset="100%" stopColor="#8b6914" />
          </linearGradient>
          <linearGradient id="goldFront" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#d4af37" />
            <stop offset="100%" stopColor="#7a5c00" />
          </linearGradient>
          <linearGradient id="goldSide" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#c9a227" />
            <stop offset="100%" stopColor="#5a4000" />
          </linearGradient>
        </defs>
        {/* top face */}
        <polygon points="8,4 56,4 60,10 4,10" fill="url(#goldTop)" />
        {/* front face */}
        <rect x="4" y="10" width="56" height="18" rx="1" fill="url(#goldFront)" />
        {/* right side */}
        <polygon points="60,10 60,28 56,28 56,10" fill="url(#goldSide)" />
        {/* shine */}
        <rect x="14" y="14" width="28" height="2" rx="1" fill="#f4d03f" fillOpacity="0.4" />
        <text x="32" y="23" textAnchor="middle" fill="#f4d03f" fillOpacity="0.7" fontSize="6" fontWeight="bold" fontFamily="serif">GOLD</text>
      </svg>
    </div>
  );
}

// ── Floating gold bars ───────────────────────────────────────────────────────
const BARS = [
  { top: '12%',  left: '4%',   opacity: 0.18, scale: 1.4,  duration: 7,  delay: 0 },
  { top: '70%',  left: '2%',   opacity: 0.12, scale: 1.0,  duration: 9,  delay: 1.5 },
  { top: '30%',  right: '3%',  opacity: 0.15, scale: 1.2,  duration: 8,  delay: 0.8 },
  { top: '75%',  right: '5%',  opacity: 0.10, scale: 0.9,  duration: 11, delay: 2 },
  { top: '50%',  left: '8%',   opacity: 0.08, scale: 0.8,  duration: 10, delay: 3 },
  { top: '20%',  right: '10%', opacity: 0.13, scale: 1.1,  duration: 6,  delay: 1 },
];

// ── Interactive chart ────────────────────────────────────────────────────────
const generateChartData = () => {
  const points = [];
  let value = 1200;
  for (let i = 0; i < 80; i++) {
    const noise = (Math.random() - 0.3) * 40;
    value = Math.max(800, value + 0.8 * 8 + noise);
    points.push({
      x: i,
      y: value,
      date: new Date(2019, 0, 1 + i * 22).toLocaleDateString('bg-BG', { day: '2-digit', month: 'short', year: 'numeric' }),
    });
  }
  return points;
};
const chartData = generateChartData();

function InteractiveChart() {
  const svgRef = useRef<SVGSVGElement>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; value: string; date: string } | null>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setAnimated(true), 400);
    return () => clearTimeout(t);
  }, []);

  const W = 900, H = 200;
  const PAD = { top: 16, right: 24, bottom: 36, left: 56 };
  const minY = Math.min(...chartData.map(d => d.y)) - 50;
  const maxY = Math.max(...chartData.map(d => d.y)) + 50;
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;

  const toX = (i: number) => PAD.left + (i / (chartData.length - 1)) * innerW;
  const toY = (v: number) => PAD.top + innerH - ((v - minY) / (maxY - minY)) * innerH;

  const linePath = chartData.map((d, i) => `${i === 0 ? 'M' : 'L'} ${toX(i)} ${toY(d.y)}`).join(' ');
  const areaPath = `M ${toX(0)} ${toY(chartData[0].y)} ` +
    chartData.map((d, i) => `L ${toX(i)} ${toY(d.y)}`).join(' ') +
    ` L ${toX(chartData.length - 1)} ${PAD.top + innerH} L ${toX(0)} ${PAD.top + innerH} Z`;

  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const mx = (e.clientX - rect.left) * (W / rect.width);
    const idx = Math.max(0, Math.min(chartData.length - 1, Math.round(((mx - PAD.left) / innerW) * (chartData.length - 1))));
    setHoverIndex(idx);
    setTooltip({ x: toX(idx), y: toY(chartData[idx].y), value: chartData[idx].y.toFixed(2), date: chartData[idx].date });
  }, []);

  const handleMouseLeave = useCallback(() => { setTooltip(null); setHoverIndex(null); }, []);

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(t => minY + t * (maxY - minY));

  return (
    <div className="relative w-full select-none">
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 200 }}
        onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
        <defs>
          <linearGradient id="areaGrad2" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#d4af37" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#d4af37" stopOpacity="0.01" />
          </linearGradient>
          <linearGradient id="lineGrad2" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#b8860b" />
            <stop offset="50%" stopColor="#f4d03f" />
            <stop offset="100%" stopColor="#d4af37" />
          </linearGradient>
          <filter id="glow2">
            <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
            <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <clipPath id="chartClip2">
            <rect x={PAD.left} y={PAD.top} width={animated ? innerW : 0} height={innerH}
              style={{ transition: 'width 2.2s cubic-bezier(0.4,0,0.2,1)' }} />
          </clipPath>
        </defs>
        {yTicks.map((tick, i) => (
          <g key={i}>
            <line x1={PAD.left} y1={toY(tick)} x2={PAD.left + innerW} y2={toY(tick)}
              stroke="#d4af37" strokeOpacity="0.07" strokeWidth="1" />
            <text x={PAD.left - 6} y={toY(tick) + 4} textAnchor="end" fill="#d4af37" fillOpacity="0.45" fontSize="9">
              {tick.toFixed(0)}
            </text>
          </g>
        ))}
        {[0, 20, 40, 60, 79].map(i => (
          <text key={i} x={toX(i)} y={PAD.top + innerH + 16} textAnchor="middle"
            fill="#d4af37" fillOpacity="0.35" fontSize="8">
            {new Date(2019, 0, 1 + i * 22).toLocaleDateString('bg-BG', { month: 'short', year: '2-digit' })}
          </text>
        ))}
        <path d={areaPath} fill="url(#areaGrad2)" clipPath="url(#chartClip2)" />
        <path d={linePath} fill="none" stroke="url(#lineGrad2)" strokeWidth="2.5"
          filter="url(#glow2)" clipPath="url(#chartClip2)" />
        {tooltip && hoverIndex !== null && (() => {
          const tx = toX(hoverIndex);
          const ty = toY(chartData[hoverIndex].y);
          const bW = 132, bH = 44;
          const bx = tx + 14 + bW > W - PAD.right ? tx - bW - 14 : tx + 14;
          const by = ty - bH / 2 < PAD.top ? PAD.top : ty - bH / 2;
          return (
            <>
              <line x1={tx} y1={PAD.top} x2={tx} y2={PAD.top + innerH}
                stroke="#d4af37" strokeOpacity="0.45" strokeWidth="1" strokeDasharray="4 3" />
              <circle cx={tx} cy={ty} r="9" fill="#d4af37" fillOpacity="0.18" />
              <circle cx={tx} cy={ty} r="5" fill="#d4af37" filter="url(#glow2)" />
              <rect x={bx} y={by} width={bW} height={bH} rx="6"
                fill="#1a1f35" stroke="#d4af37" strokeOpacity="0.55" strokeWidth="1" />
              <text x={bx + 10} y={by + 16} fill="#d4af37" fontSize="10" fontWeight="bold">{chartData[hoverIndex].date}</text>
              <text x={bx + 10} y={by + 33} fill="#f4d03f" fontSize="13" fontWeight="bold">
                $ {parseFloat(tooltip.value).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </text>
            </>
          );
        })()}
      </svg>
    </div>
  );
}

// ── Ticker strip ─────────────────────────────────────────────────────────────
function TickerStrip() {
  const doubled = [...TICKERS, ...TICKERS];
  return (
    <div className="w-full overflow-hidden border-t border-b border-[#d4af37]/15 bg-[#0a0e1a]/70 backdrop-blur-sm py-2">
      <div className="flex gap-0 ticker-scroll">
        {doubled.map((t, i) => (
          <div key={i} className="flex items-center gap-2 px-6 whitespace-nowrap">
            <span className="text-gray-400 text-xs font-mono tracking-wider">{t.symbol}</span>
            <span className="text-white text-xs font-semibold font-mono">{t.price}</span>
            <span className={`text-xs font-bold font-mono ${t.up ? 'text-emerald-400' : 'text-red-400'}`}>
              {t.up ? '▲' : '▼'} {t.change}
            </span>
            <span className="text-[#d4af37]/20 text-xs">|</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main Hero ────────────────────────────────────────────────────────────────
export default function Hero() {
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useLang();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hero" className="relative min-h-screen flex flex-col items-center justify-center pt-20 overflow-hidden">
      {/* Ticker strip at top */}
      <div className="absolute top-[72px] left-0 right-0 z-20">
        <TickerStrip />
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0e1a]/40 to-[#0a0e1a]"></div>

      {/* Floating gold bars */}
      {BARS.map((b, i) => (
        <GoldBar
          key={i}
          style={{
            top: b.top,
            left: (b as any).left,
            right: (b as any).right,
            opacity: b.opacity,
            transform: `scale(${b.scale})`,
            animation: `floatBar ${b.duration}s ease-in-out ${b.delay}s infinite alternate`,
          }}
        />
      ))}

      {/* Live quote cards floating */}
      <div className="absolute top-[130px] left-6 z-10 hidden lg:flex flex-col gap-3">
        {[
          { sym: 'XAU/USD', val: '2 341.80', chg: '+1.24%', up: true },
          { sym: 'BTC/USD', val: '67 420', chg: '+3.41%', up: true },
          { sym: 'S&P 500', val: '5 248.30', chg: '-0.32%', up: false },
        ].map((q, i) => (
          <div
            key={i}
            className="bg-[#1a1f35]/80 backdrop-blur-sm border border-[#d4af37]/20 rounded-xl px-4 py-3 min-w-[140px]"
            style={{ animation: `floatBar ${6 + i}s ease-in-out ${i * 0.7}s infinite alternate` }}
          >
            <div className="text-[#d4af37] text-xs font-mono font-bold tracking-wider mb-1">{q.sym}</div>
            <div className="text-white text-sm font-semibold font-mono">{q.val}</div>
            <div className={`text-xs font-bold font-mono mt-0.5 ${q.up ? 'text-emerald-400' : 'text-red-400'}`}>
              {q.up ? '▲' : '▼'} {q.chg}
            </div>
          </div>
        ))}
      </div>

      <div className="absolute top-[130px] right-6 z-10 hidden lg:flex flex-col gap-3">
        {[
          { sym: 'EUR/USD', val: '1.0842', chg: '+0.18%', up: true },
          { sym: 'SILVER',  val: '27.43',  chg: '+2.05%', up: true },
          { sym: 'WTI OIL', val: '82.14',  chg: '-0.87%', up: false },
        ].map((q, i) => (
          <div
            key={i}
            className="bg-[#1a1f35]/80 backdrop-blur-sm border border-[#d4af37]/20 rounded-xl px-4 py-3 min-w-[140px]"
            style={{ animation: `floatBar ${7 + i}s ease-in-out ${i * 0.9 + 0.4}s infinite alternate` }}
          >
            <div className="text-[#d4af37] text-xs font-mono font-bold tracking-wider mb-1">{q.sym}</div>
            <div className="text-white text-sm font-semibold font-mono">{q.val}</div>
            <div className={`text-xs font-bold font-mono mt-0.5 ${q.up ? 'text-emerald-400' : 'text-red-400'}`}>
              {q.up ? '▲' : '▼'} {q.chg}
            </div>
          </div>
        ))}
      </div>

      {/* Main content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center w-full">
        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="mb-8 mt-16">
            <img
              src="https://static.readdy.ai/image/5490f3a4ad0e9eb55a0769efce385370/59278221c6b2727f8d1b3dc0934be840.jpeg"
              alt="Алекс Зингер"
              className="w-40 h-40 rounded-full object-cover border-4 border-[#d4af37] mx-auto shadow-2xl shadow-[#d4af37]/30"
            />
          </div>

          <h1 className="text-6xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-[#d4af37] via-[#f4d03f] to-[#d4af37] bg-clip-text text-transparent">
            {t('hero_title')}
          </h1>

          <p className="text-2xl md:text-3xl text-gray-300 mb-4 font-light">
            {t('hero_subtitle')}
          </p>

          <p className="text-lg text-gray-400 mb-8 max-w-2xl mx-auto">
            {t('hero_desc')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <button
              onClick={scrollToContact}
              className="px-8 py-4 bg-gradient-to-r from-[#d4af37] to-[#f4d03f] text-[#0a0e1a] font-bold text-lg rounded-lg hover:shadow-2xl hover:shadow-[#d4af37]/50 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              {t('hero_cta')}
            </button>
            <a
              href="#about"
              className="px-8 py-4 border-2 border-[#d4af37] text-[#d4af37] font-semibold text-lg rounded-lg hover:bg-[#d4af37]/10 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {t('hero_learn')}
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto mb-12">
            {[
              { val: '25+', labelKey: 'hero_years' },
              { val: '500+', labelKey: 'hero_clients' },
              { val: '45+', labelKey: 'hero_countries' },
            ].map((s, i) => (
              <div key={i} className="bg-gradient-to-br from-[#1a1f35] to-[#0a0e1a] p-6 rounded-xl border border-[#d4af37]/20 hover:border-[#d4af37]/50 transition-all duration-300">
                <div className="text-4xl font-bold text-[#d4af37] mb-2">{s.val}</div>
                <div className="text-gray-300">{t(s.labelKey)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CSS animations */}
      <style>{`
        @keyframes floatBar {
          0%   { transform: translateY(0px) rotate(-2deg); }
          100% { transform: translateY(-18px) rotate(2deg); }
        }
        .ticker-scroll {
          animation: tickerMove 28s linear infinite;
          width: max-content;
        }
        @keyframes tickerMove {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
}
